import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const CodeExplainer = ({ snippet }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(3000); // 3 seconds per step
  const [maxCharsPerLine, setMaxCharsPerLine] = useState(80); // Default fallback
  const codeContainerRef = useRef(null);

  const codeLines = snippet.code.split('\n');
  const totalSteps = snippet.explanations?.length || codeLines.length;

  // Initialize step from URL on mount
  useEffect(() => {
    const stepParam = searchParams.get('step');
    if (stepParam) {
      const step = parseInt(stepParam, 10);
      if (!isNaN(step) && step >= 0 && step < totalSteps) {
        setCurrentStep(step);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Update URL when step changes
  useEffect(() => {
    const currentStepParam = searchParams.get('step');
    const newStep = currentStep.toString();
    
    if (currentStepParam !== newStep) {
      setSearchParams(params => {
        params.set('step', newStep);
        return params;
      }, { replace: true }); // Use replace to avoid cluttering browser history
    }
  }, [currentStep, searchParams, setSearchParams]);

  // Calculate max characters per line based on container width
  useEffect(() => {
    const calculateMaxChars = () => {
      if (codeContainerRef.current) {
        const containerWidth = codeContainerRef.current.offsetWidth;
        // Account for padding (40px total), line numbers (~60px), and scrollbar (~16px)
        const availableWidth = containerWidth - 116;
        // Monospace character width at 18px is approximately 11px
        const charsPerLine = Math.floor(availableWidth / 11);
        setMaxCharsPerLine(Math.max(60, charsPerLine)); // Minimum 60 chars
      }
    };

    calculateMaxChars();
    window.addEventListener('resize', calculateMaxChars);
    return () => window.removeEventListener('resize', calculateMaxChars);
  }, []);

  // Word wrap text to specified width
  const wordWrap = (text, maxWidth = 75) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
      if (currentLine.length + word.length + 1 <= maxWidth) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    
    if (currentLine) lines.push(currentLine);
    return lines;
  };

  // Format explanation as comment based on language
  const formatExplanationAsComment = (explanation, keyPoints, language) => {
    const lines = [];
    
    // Choose comment style based on language
    const isHTML = language === 'html';
    const isPythonOrRuby = language === 'python' || language === 'ruby';
    
    // Calculate wrap width with some buffer for comment syntax
    const commentPrefixLength = isHTML ? 2 : (isPythonOrRuby ? 2 : 3); // '  ', '# ', or ' * '
    const wrapWidth = maxCharsPerLine - commentPrefixLength;
    
    // Wrap explanation text
    const explanationLines = explanation.split('\n');
    const wrappedExplanation = [];
    explanationLines.forEach(line => {
      const wrapped = wordWrap(line.trim(), wrapWidth);
      wrappedExplanation.push(...wrapped);
    });
    
    // Wrap key points
    const wrappedKeyPoints = [];
    if (keyPoints && keyPoints.length > 0) {
      keyPoints.forEach(point => {
        const wrapped = wordWrap(point.trim(), wrapWidth - 2); // Leave space for "- "
        wrappedKeyPoints.push(...wrapped);
      });
    }
    
    if (isHTML) {
      lines.push('<!--');
      lines.push('  EXPLANATION:');
      wrappedExplanation.forEach(line => lines.push('  ' + line));
      if (wrappedKeyPoints.length > 0) {
        lines.push('');
        lines.push('  KEY POINTS:');
        let isNewPoint = true;
        wrappedKeyPoints.forEach((line) => {
          if (isNewPoint) {
            lines.push('  - ' + line);
            isNewPoint = line.length < wrapWidth - 4;
          } else {
            lines.push('    ' + line);
            isNewPoint = line.length < wrapWidth - 4;
          }
        });
      }
      lines.push('-->');
    } else if (isPythonOrRuby) {
      lines.push('# EXPLANATION:');
      wrappedExplanation.forEach(line => lines.push('# ' + line));
      if (wrappedKeyPoints.length > 0) {
        lines.push('#');
        lines.push('# KEY POINTS:');
        let isNewPoint = true;
        wrappedKeyPoints.forEach((line) => {
          if (isNewPoint) {
            lines.push('# - ' + line);
            isNewPoint = line.length < wrapWidth - 4;
          } else {
            lines.push('#   ' + line);
            isNewPoint = line.length < wrapWidth - 4;
          }
        });
      }
    } else {
      lines.push('/*');
      lines.push(' * EXPLANATION:');
      wrappedExplanation.forEach(line => lines.push(' * ' + line));
      if (wrappedKeyPoints.length > 0) {
        lines.push(' *');
        lines.push(' * KEY POINTS:');
        let isNewPoint = true;
        wrappedKeyPoints.forEach((line) => {
          if (isNewPoint) {
            lines.push(' * - ' + line);
            isNewPoint = line.length < wrapWidth - 5;
          } else {
            lines.push(' *   ' + line);
            isNewPoint = line.length < wrapWidth - 5;
          }
        });
      }
      lines.push(' */');
    }
    
    return lines.join('\n');
  };

  // Format collapsed comment based on language
  const formatCollapsedComment = (language) => {
    const isHTML = language === 'html';
    const isPythonOrRuby = language === 'python' || language === 'ruby';
    
    if (isHTML) {
      return '<!-- ... -->';
    } else if (isPythonOrRuby) {
      return '# ...';
    } else {
      return '/* ... */';
    }
  };

  // Get code up to current step with explanations as comments
  const getCurrentCode = () => {
    if (!snippet.explanations || snippet.explanations.length === 0) {
      return snippet.code;
    }
    
    const output = [];
    let lastLineNumber = 0;
    
    // Loop through all steps up to current
    for (let step = 0; step <= currentStep; step++) {
      const explanation = snippet.explanations[step];
      const lineNumber = explanation.lineNumber;
      
      // Add any code lines between last explanation and this one (non-explained lines)
      for (let i = lastLineNumber + 1; i < lineNumber; i++) {
        output.push(codeLines[i - 1]); // i is 1-based, array is 0-based
      }
      
      // Add the current line of code being explained
      output.push(codeLines[lineNumber - 1]); // lineNumber is 1-based, array is 0-based
      
      // Add comment below the line
      if (step < currentStep) {
        // Collapsed comment for previous steps
        output.push(formatCollapsedComment(snippet.programmingLanguage));
      } else {
        // Full comment for current step
        const commentBlock = formatExplanationAsComment(
          explanation.explanation,
          explanation.keyPoints,
          snippet.programmingLanguage
        );
        output.push(commentBlock);
      }
      
      output.push(''); // Empty line separator
      lastLineNumber = lineNumber;
    }
    
    return output.join('\n');
  };

  // Get number of lines being displayed (including comments)
  const getDisplayedLineCount = () => {
    // Calculate which display line number corresponds to the current code line
    if (!snippet.explanations || snippet.explanations.length === 0) {
      return codeLines.length;
    }
    
    const currentExplanation = snippet.explanations[currentStep];
    let displayLineNumber = 0;
    let lastLineNumber = 0;
    
    // Count lines up to and including current step
    for (let step = 0; step <= currentStep; step++) {
      const explanation = snippet.explanations[step];
      const lineNumber = explanation.lineNumber;
      
      // Count any non-explained code lines between last explanation and this one
      const gapLines = lineNumber - lastLineNumber - 1;
      displayLineNumber += gapLines;
      
      // Count the current code line
      displayLineNumber += 1;
      
      // If this is the current step, return this line number (before adding comment)
      if (step === currentStep) {
        return displayLineNumber;
      }
      
      // Add lines for previous step's collapsed comment + empty line
      displayLineNumber += 2; // collapsed comment (1 line) + empty line (1 line)
      
      lastLineNumber = lineNumber;
    }
    
    return displayLineNumber;
  };

  // Get current line explanation
  const getCurrentExplanation = () => {
    return snippet.explanations?.[currentStep] || null;
  };

  // Auto-play effect
  useEffect(() => {
    let interval;
    if (isPlaying && currentStep < totalSteps - 1) {
      interval = setInterval(() => {
        setCurrentStep(prev => {
          if (prev >= totalSteps - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, playSpeed);
    } else if (currentStep >= totalSteps - 1) {
      setIsPlaying(false);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentStep, totalSteps, playSpeed]);

  // Keyboard navigation (arrow keys)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle arrow keys when not typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, totalSteps]); // Dependencies so handleNext/handlePrevious have latest values

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const getSyntaxHighlighterLanguage = (lang) => {
    const languageMap = {
      'javascript': 'javascript',
      'typescript': 'typescript',
      'python': 'python',
      'java': 'java',
      'html': 'markup',
      'css': 'css',
      'sql': 'sql',
      'csharp': 'csharp',
      'cpp': 'cpp',
      'php': 'php',
      'ruby': 'ruby',
      'go': 'go',
      'rust': 'rust',
      'kotlin': 'kotlin',
      'swift': 'swift'
    };
    return languageMap[lang] || 'javascript';
  };

  if (!snippet.explanations || snippet.explanations.length === 0) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        backgroundColor: '#fffbeb',
        border: '1px solid #fbbf24',
        borderRadius: '8px',
        color: '#92400e'
      }}>
        <p style={{ fontSize: '16px', margin: 0 }}>
          No explanations available for this code snippet. Please generate explanations first.
        </p>
      </div>
    );
  }

  const currentExplanation = getCurrentExplanation();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Controls */}
      <div className="card" style={{ padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '18px' }}>Interactive Code Explainer</h3>
            <p style={{ margin: 0, fontSize: '13px', color: '#718096' }}>
              Step {currentStep + 1} of {totalSteps}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '13px', color: '#718096', marginRight: '8px' }}>
              Speed:
              <select
                value={playSpeed}
                onChange={(e) => setPlaySpeed(Number(e.target.value))}
                style={{
                  marginLeft: '8px',
                  padding: '4px 8px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  fontSize: '13px'
                }}
              >
                <option value={1500}>Fast (1.5s)</option>
                <option value={3000}>Normal (3s)</option>
                <option value={5000}>Slow (5s)</option>
              </select>
            </label>
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{
          width: '100%',
          height: '6px',
          backgroundColor: '#e2e8f0',
          borderRadius: '3px',
          overflow: 'hidden',
          marginBottom: '16px'
        }}>
          <div style={{
            width: `${((currentStep + 1) / totalSteps) * 100}%`,
            height: '100%',
            backgroundColor: '#667eea',
            transition: 'width 0.3s ease'
          }} />
        </div>

        {/* Control Buttons */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
          <button
            onClick={handleReset}
            className="btn btn-secondary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              fontSize: '14px'
            }}
          >
            <RestartAltIcon fontSize="small" />
            Reset
          </button>
          
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="btn btn-secondary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              fontSize: '14px',
              opacity: currentStep === 0 ? 0.5 : 1,
              cursor: currentStep === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            <NavigateBeforeIcon fontSize="small" />
            Previous
          </button>

          <button
            onClick={handlePlayPause}
            className="btn btn-primary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 24px',
              fontSize: '14px'
            }}
          >
            {isPlaying ? (
              <>
                <PauseIcon fontSize="small" />
                Pause
              </>
            ) : (
              <>
                <PlayArrowIcon fontSize="small" />
                Play
              </>
            )}
          </button>

          <button
            onClick={handleNext}
            disabled={currentStep >= totalSteps - 1}
            className="btn btn-secondary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              fontSize: '14px',
              opacity: currentStep >= totalSteps - 1 ? 0.5 : 1,
              cursor: currentStep >= totalSteps - 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Next
            <NavigateNextIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* Code Display with Explanation as Comment */}
      <div className="card" style={{ padding: '20px' }}>
        <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#2d3748' }}>
          Code with Explanation (Step {currentStep + 1} of {totalSteps})
        </h4>
        <div ref={codeContainerRef} style={{ position: 'relative' }}>
          <SyntaxHighlighter
            language={getSyntaxHighlighterLanguage(snippet.programmingLanguage)}
            style={vscDarkPlus}
            showLineNumbers
            wrapLines
            lineProps={(lineNumber) => {
              // Highlight the last line of the displayed code (current line being explained)
              const displayedLineCount = getDisplayedLineCount();
              const isCurrentLine = lineNumber === displayedLineCount;
              return {
                style: {
                  backgroundColor: isCurrentLine ? 'rgba(255, 255, 0, 0.2)' : 'transparent',
                  display: 'block',
                  width: '100%'
                }
              };
            }}
            startingLineNumber={1}
            customStyle={{
              margin: 0,
              borderRadius: '8px',
              fontSize: '18px',
              minHeight: '400px',
              maxHeight: '600px',
              overflow: 'auto'
            }}
          >
            {getCurrentCode()}
          </SyntaxHighlighter>
        </div>
      </div>

      {/* Completion Message */}
      {currentStep >= totalSteps - 1 && (
        <div style={{
          padding: '20px',
          backgroundColor: '#d1fae5',
          border: '2px solid #10b981',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 8px 0', color: '#065f46', fontSize: '18px' }}>
            🎉 Code Walkthrough Complete!
          </h3>
          <p style={{ margin: '0 0 16px 0', color: '#047857', fontSize: '14px' }}>
            You've reviewed all {totalSteps} lines of code.
          </p>
          <button
            onClick={handleReset}
            className="btn btn-primary"
            style={{ padding: '10px 24px' }}
          >
            Start Over
          </button>
        </div>
      )}
    </div>
  );
};

export default CodeExplainer;
